/**
 * Convert non-negative counts into integer percentages that always sum to 100 (or all 0 if total is 0).
 * Uses the largest-remainder method so rounding each share independently cannot make the total exceed 100.
 */
export function partitionCountsAsIntegerPercentages(counts: Record<string, number>): Record<string, number> {
  const sanitizedEntries = Object.entries(counts).map(([k, v]) => [k, Math.max(0, Number(v) || 0)] as const);
  const total = sanitizedEntries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) {
    return Object.fromEntries(sanitizedEntries.map(([k]) => [k, 0]));
  }

  type Part = { key: string; floor: number; frac: number };
  const parts: Part[] = sanitizedEntries.map(([key, count]) => {
    const exact = (count / total) * 100;
    const floor = Math.floor(exact + Number.EPSILON);
    return { key, floor, frac: exact - floor };
  });

  const result: Record<string, number> = {};
  let sumFloors = 0;
  for (const p of parts) {
    result[p.key] = p.floor;
    sumFloors += p.floor;
  }
  const remainder = 100 - sumFloors;
  const order = [...parts].sort((a, b) => {
    if (b.frac !== a.frac) return b.frac - a.frac;
    return a.key.localeCompare(b.key);
  });
  for (let i = 0; i < remainder; i++) {
    result[order[i]!.key]++;
  }
  return result;
}
